/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useRef, useState } from 'react';
import Autocomplete from '@mui/material/Autocomplete';

import { Box, capitalize, CircularProgress, InputAdornment } from '@mui/material';
import { useTheme } from '../../../styles';
import { ModelListItem } from '../ListItem/ModelListItem';
import { AutocompleteContainer, AutocompleteInput, AutocompletePopper } from '../Base';
import { useDispatch, useSelector } from 'react-redux';
import { Command } from '@store/slices';
import { State, tabActions } from '@store/index';
import { ErrorMessage } from '../Models';
import { useParams } from 'react-router-dom';
import { NodeState } from '@components/Flow/Nodes';

interface Props {
  value: string;
  onChange: (event: React.SyntheticEvent<Element, Event>, newValue: any | null) => void;
  disabled?: boolean;
  loading?: boolean;
  placeholder?: string;
  command: Command;
}

export type NodeOption = {
  id: string;
  trigger: boolean;
  label: string;
  value: string;
  metadata: {
    command: Command;
    node_id: string;
    color: string;
    group: string;
  };
};

export const NodeSelector: React.FC<Props> = ({ value, onChange, disabled, placeholder, loading, command }) => {
  const autocompleteRef = useRef(null);
  const theme = useTheme();
  const dispatch = useDispatch();

  const { workflow_id } = useParams();

  const themeType = useSelector((state: State) => state.flow.theme);
  const commands = useSelector((state: State) => state.tabs.active.commands);
  const modules = useSelector((state: State) => state.tabs.active.modules);
  const nodeErrors = useSelector((state: State) => state.tabs.active.nodeErrors);

  const options: NodeOption[] = commands
    .map((command) => {
      const node = command.node;
      const displayFieldId = command.fields.find((field) => field.display_name)?.id;
      const displayName = displayFieldId ? (command.state[displayFieldId as keyof NodeState] as string) : null;

      const modNode = modules.find((mod) => mod?.flow_id === command?.state?.moduleId);
      const moduleName = modNode?.label;

      const group = capitalize(command.config.command_id.split('.')[0] || '');
      const color =
        themeType === 'dark' && command.config.background === '#000000'
          ? theme.telnyx.citron
          : command.config.background;

      return {
        id: node.id,
        trigger: Boolean(command.config.trigger),
        label: moduleName || displayName || command.config.title,
        value: node.id,
        metadata: {
          command,
          node_id: node.id,
          color,
          group,
        },
      };
    })
    .filter((option) => {
      if (option?.metadata?.command?.config?.command_id?.includes('logic')) return false;
      if (option?.metadata?.command?.config?.command_id === 'llm.tool') return false;
      if (option.trigger) return false;
      return true;
    });

  const [open, setOpen] = useState<boolean>(false);

  const error = nodeErrors?.find(
    (nodeError) =>
      nodeError.id === command.node.id &&
      nodeError?.workflowId === workflow_id &&
      nodeError?.fields?.find((nodeField) => nodeField.id === 'node_id')
  );
  const errorField = error ? error?.fields?.find((nodeField) => nodeField.id === 'node_id') : undefined;
  const errorFieldMessage = errorField ? errorField.message : undefined;

  const onChangeInput = (event: React.SyntheticEvent<Element, Event>, newValue: NodeOption | null) => {
    onChange(event, newValue || { value: '', label: '' });
    setOpen(false);
    if (newValue) (document?.activeElement as HTMLElement)?.blur();

    dispatch(
      tabActions.nodeJumpCopyState({
        source_node_id: command.node.id,
        target_node_id: newValue?.metadata.node_id || '',
      })
    );
  };

  return (
    <>
      <AutocompleteContainer open={open}>
        <Autocomplete
          value={options.find((item) => item?.value === value) || null}
          ref={autocompleteRef}
          onChange={(event, newValue) => onChangeInput(event, newValue)}
          disabled={disabled}
          PopperComponent={AutocompletePopper}
          // groupBy={(option) => option?.metadata?.group}
          renderOption={(props, option) => <ModelListItem {...props} option={option} />}
          autoHighlight
          options={options}
          disablePortal
          getOptionLabel={(option) => option?.label || ''}
          renderInput={(params) => (
            <AutocompleteInput
              open={open}
              ref={params.InputProps.ref}
              inputProps={params.inputProps}
              placeholder={placeholder}
              disabled={disabled}
              onFocus={() => setOpen(true)}
              error={Boolean(errorFieldMessage)}
              onBlur={() => setOpen(false)}
              endAdornment={loading ? <CircularProgress sx={{ color: theme.primary }} size={14} /> : null}
              startAdornment={
                value &&
                !loading && (
                  <InputAdornment position='start'>
                    <Box
                      component='span'
                      sx={{
                        width: 12,
                        height: 12,
                        flexShrink: 0,
                        borderRadius: '3px',
                        mr: 0.35,
                        ml: 0.31,
                        mt: '0px',
                      }}
                      style={{ backgroundColor: options.find((label) => label?.value === value)?.metadata?.color }}
                    />
                  </InputAdornment>
                )
              }
            />
          )}
        />
      </AutocompleteContainer>
      {errorFieldMessage && <ErrorMessage>{errorFieldMessage}</ErrorMessage>}
    </>
  );
};
